<template>
  <div v-if="blok.text || blok.image" v-editable="blok" class="text-illustrated">
    <div class="text-illustrated-grid" :style="gridStyle">
      <img
        v-if="blok.image"
        :src="blok.image.filename"
        :alt="blok.imageAlt || blok.image.alt || ''"
        class="text-illustrated-image"
        :style="{ order: imageOrder }"
      />
      <div class="text-illustrated-content" :style="{ order: contentOrder }">
        <StoryblokRichText v-if="richTextBlok" :blok="richTextBlok" />
        <p v-else-if="plainText">{{ plainText }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import StoryblokRichText from '@/features/storyblok/components/StoryblokRichText.vue';
import {
  type StoryblokRichTextBlok,
  type StoryblokTextIllustratedBlok,
} from '@/features/storyblok/types';

const props = defineProps<{
  blok: StoryblokTextIllustratedBlok;
}>();

const richTextBlok = computed<StoryblokRichTextBlok | null>(() =>
  typeof props.blok.text === 'string' ? null : { content: props.blok.text }
);

const plainText = computed(() =>
  typeof props.blok.text === 'string' ? props.blok.text.trim() : undefined
);

const position = computed(() => props.blok.imagePosition ?? 'left');

const frWeights = computed<[number, number]>(() => {
  const match = props.blok.grid?.match(/cols-(\d+)-(\d+)/);
  return match ? [Number(match[1]), Number(match[2])] : [30, 70];
});

const gridStyle = computed(() => {
  const [imgFr, contentFr] = frWeights.value;
  if (position.value === 'left' || position.value === 'right') {
    const cols =
      position.value === 'left' ? `${imgFr}fr ${contentFr}fr` : `${contentFr}fr ${imgFr}fr`;
    return { gridTemplateColumns: cols };
  }
  return { gridTemplateColumns: '1fr' };
});

const imageOrder = computed(() =>
  position.value === 'right' || position.value === 'bottom' ? 2 : 1
);
const contentOrder = computed(() =>
  position.value === 'right' || position.value === 'bottom' ? 1 : 2
);
</script>

<style scoped>
.text-illustrated-grid {
  display: grid;
  gap: var(--ion-space-4);
  padding-bottom: var(--ion-space-6);
  align-items: start;
}

.text-illustrated-image {
  width: 100%;
  max-height: 360px;
  object-fit: cover;
  border-radius: var(--ion-radius-2xl);
}

.text-illustrated-content p {
  margin: 0;
}

.text-illustrated-content :deep(.rich-text) {
  width: 100%;
  color: var(--ion-text-color);
}

@media (max-width: 768px) {
  .text-illustrated-grid {
    grid-template-columns: 1fr !important;
  }

  .text-illustrated-image {
    order: 1 !important;
  }

  .text-illustrated-content {
    order: 2 !important;
  }
}
</style>
